import { RefreshCw, WifiOff } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

interface PwaContextValue {
  canInstall: boolean;
  checkingConnection: boolean;
  install: () => Promise<void>;
  isOnline: boolean;
  isStandalone: boolean;
  retryConnection: () => Promise<boolean>;
}

const PwaContext = createContext<PwaContextValue | null>(null);

function detectStandalone() {
  const navigatorWithStandalone = navigator as Navigator & { standalone?: boolean };
  return window.matchMedia("(display-mode: standalone)").matches || navigatorWithStandalone.standalone === true;
}

export function PwaProvider({ children }: { children: ReactNode }) {
  // Start optimistically. navigator.onLine reports the network adapter state,
  // not whether our API is reachable, and can briefly be false when a laptop
  // wakes or switches Wi-Fi.
  const [isOnline, setIsOnline] = useState(true);
  const [checkingConnection, setCheckingConnection] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(null);
  const [isStandalone, setIsStandalone] = useState(detectStandalone);
  const failedChecks = useRef(0);
  const activeCheck = useRef<Promise<boolean> | null>(null);

  const retryConnection = useCallback(async () => {
    if (activeCheck.current) return activeCheck.current;

    const check = async () => {
      setCheckingConnection(true);
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 8_000);

      try {
        const response = await fetch("/health", {
          cache: "no-store",
          credentials: "same-origin",
          headers: { "X-Qadim-Connectivity": "1" },
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Health ${response.status}`);
        failedChecks.current = 0;
        setIsOnline(true);
        return true;
      } catch {
        failedChecks.current += 1;
        // A single timeout or Wi-Fi handover must not throw the user out of
        // the CRM. Only show offline state after a repeated confirmed failure.
        if (failedChecks.current >= 2) setIsOnline(false);
        return false;
      } finally {
        window.clearTimeout(timeout);
        setCheckingConnection(false);
        activeCheck.current = null;
      }
    };

    activeCheck.current = check();
    return activeCheck.current;
  }, []);

  useEffect(() => {
    const handleOffline = () => void retryConnection();
    const handleOnline = () => {
      failedChecks.current = 0;
      setIsOnline(true);
      void retryConnection();
    };
    const handleInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as InstallPromptEvent);
    };
    const handleInstalled = () => {
      setInstallPrompt(null);
      setIsStandalone(true);
    };
    const displayMode = window.matchMedia("(display-mode: standalone)");
    const handleDisplayMode = () => setIsStandalone(detectStandalone());

    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);
    window.addEventListener("beforeinstallprompt", handleInstallPrompt);
    window.addEventListener("appinstalled", handleInstalled);
    displayMode.addEventListener("change", handleDisplayMode);

    void retryConnection();
    const connectivityInterval = window.setInterval(() => void retryConnection(), 15_000);

    return () => {
      window.clearInterval(connectivityInterval);
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("beforeinstallprompt", handleInstallPrompt);
      window.removeEventListener("appinstalled", handleInstalled);
      displayMode.removeEventListener("change", handleDisplayMode);
    };
  }, [retryConnection]);

  useEffect(() => {
    document.documentElement.dataset.network = isOnline ? "online" : "offline";
    return () => {
      delete document.documentElement.dataset.network;
    };
  }, [isOnline]);

  const install = useCallback(async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  }, [installPrompt]);

  const value = useMemo<PwaContextValue>(() => ({
    canInstall: Boolean(installPrompt) && !isStandalone,
    checkingConnection,
    install,
    isOnline,
    isStandalone,
    retryConnection,
  }), [checkingConnection, install, installPrompt, isOnline, isStandalone, retryConnection]);

  return (
    <PwaContext.Provider value={value}>
      {children}
      {!isOnline && (
        <div className="offline-banner" role="status" aria-live="assertive">
          <span className="offline-banner__icon"><WifiOff size={18} /></span>
          <div>
            <strong>Нет связи с сервером</strong>
            <span>Проверьте интернет. Данные не обновляются, а изменения временно недоступны.</span>
          </div>
          <button type="button" onClick={() => void retryConnection()} disabled={checkingConnection}>
            <RefreshCw size={15} className={checkingConnection ? "is-spinning" : ""} />
            {checkingConnection ? "Проверяем…" : "Проверить"}
          </button>
        </div>
      )}
    </PwaContext.Provider>
  );
}

export function usePwa() {
  const value = useContext(PwaContext);
  if (!value) throw new Error("usePwa must be used inside PwaProvider");
  return value;
}
